import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import { Button } from '../components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { User, FileDown, Trash2, ShieldCheck } from 'lucide-react';

const spring = { type: 'spring', stiffness: 300, damping: 20 };

export default function AccountPage() {
  const { user, logout } = useAuth();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const { t } = useI18n();

  const exportData = async () => {
    try {
      const r = await api.get('/privacy/export', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'mes-donnees-oncroitquonjoue.json';
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('account.exportOk'));
    } catch { toast.error(t('account.exportError')); }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete('/privacy/account');
      toast.success(t('account.deleteOk'));
      logout();
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="account-page">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={spring}>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-creme">{t('account.title')}</h1>
        <p className="text-base sm:text-lg text-brume mt-3">{t('account.subtitle')}</p>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.1 }}
        className="bg-nuit-800 border border-nuit-700 rounded-3xl p-8 mt-10" data-testid="account-info-card">
        <h2 className="text-xl font-semibold text-creme flex items-center gap-2 mb-5">
          <User className="w-5 h-5 text-or-400" strokeWidth={2.5} /> {t('account.profile')}
        </h2>
        <div className="space-y-2 text-base">
          <p className="text-brume">{t('account.name')} : <strong className="text-creme">{user?.name}</strong></p>
          <p className="text-brume">{t('account.email')} : <strong className="text-creme">{user?.email}</strong></p>
          <p className="text-brume">{t('account.role')} : <strong className="text-creme">{t(`roles.${user?.role}`, undefined, user?.role)}</strong></p>
          {user?.age && <p className="text-brume">{t('account.age')} : <strong className="text-creme">{t('account.ageValue', { age: user.age })}</strong></p>}
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.15 }}
        className="bg-nuit-800 border border-nuit-700 rounded-3xl p-8 mt-6" data-testid="account-export-card">
        <h2 className="text-xl font-semibold text-creme flex items-center gap-2 mb-3">
          <FileDown className="w-5 h-5 text-or-400" strokeWidth={2.5} /> {t('account.exportTitle')}
        </h2>
        <p className="text-sm text-brume leading-relaxed mb-5">
          {t('account.exportText')}
        </p>
        <Button onClick={exportData} data-testid="export-data-button"
          className="rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold gap-2">
          <FileDown className="w-4 h-4" strokeWidth={2.5} /> {t('account.exportBtn')}
        </Button>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.2 }}
        className="bg-nuit-800 border border-rose-900/60 rounded-3xl p-8 mt-6" data-testid="account-delete-card">
        <h2 className="text-xl font-semibold text-rose-300 flex items-center gap-2 mb-3">
          <Trash2 className="w-5 h-5" strokeWidth={2.5} /> {t('account.deleteTitle')}
        </h2>
        <p className="text-sm text-brume leading-relaxed mb-5">
          {t('account.deleteText')}
        </p>
        <Button onClick={() => setConfirmDelete(true)} data-testid="delete-account-button"
          className="rounded-full bg-rose-500 hover:bg-rose-400 text-white font-bold gap-2">
          <Trash2 className="w-4 h-4" strokeWidth={2.5} /> {t('account.deleteBtn')}
        </Button>
      </motion.div>

      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
        className="text-xs text-brume mt-8 flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-or-400 shrink-0" />
        {t('account.rgpdNote')}
      </motion.p>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="rounded-3xl bg-nuit-800 border-nuit-700" data-testid="delete-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-fredoka text-2xl text-creme">{t('account.deleteDialogTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="text-brume">
              {t('account.deleteDialogText')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-account-cancel"
              className="rounded-full bg-transparent border-2 border-nuit-600 text-brume font-bold hover:bg-nuit-700 hover:text-creme">
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={deleteAccount} disabled={deleting} data-testid="delete-account-confirm"
              className="rounded-full bg-rose-500 hover:bg-rose-400 text-white font-bold">
              {deleting ? t('account.deleting') : t('account.deleteConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
